<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '../lib/api'
  import { addToast } from '../lib/toast'
  import { fade, slide } from 'svelte/transition'

  // Tipos
  interface Session {
    id: string
    label: string
    number: string
    type: string
    connected: boolean
    active: boolean
  }

  interface PoolMember {
    id: string
    pool_id: string
    session_id: string
    priority: number
    weight: number
    active: boolean
    session?: Session
  }

  interface Pool {
    id: string
    user_id: string
    name: string
    description: string
    mode: 'random' | 'round-robin' | 'least-loaded' | 'priority'
    session_type: string
    active: boolean
    created_at: string
    updated_at: string
    members?: PoolMember[]
  }

  // Estado
  let pools: Pool[] = []
  let sessions: Session[] = []
  let loading = false
  let selectedPool: Pool | null = null
  let showCreateModal = false
  let showAddMemberModal = false
  let showStatsModal = false
  let poolStats: Record<string, number> = {}

  // Formularios
  let createForm: { name: string; description: string; mode: string; session_type: string } = {
    name: '',
    description: '',
    mode: 'priority',
    session_type: 'whatsapp'
  }

  let addMemberForm = {
    session_id: '',
    priority: 1
  }

  const modes = [
    { value: 'priority', label: 'Por Prioridad', description: 'Usa siempre el dispositivo principal (prioridad 1). Si falla, pasa al siguiente. Ideal para tener un dispositivo de respaldo.' },
    { value: 'round-robin', label: 'Por Turnos', description: 'Distribuye los mensajes en orden rotativo: A, B, C, A, B, C... Equilibra la carga de forma exacta.' },
    { value: 'least-loaded', label: 'Menos Cargado', description: 'Envía siempre al dispositivo que ha procesado menos mensajes. Equilibra la carga de forma inteligente.' },
    { value: 'random', label: 'Aleatorio', description: 'Elige un dispositivo al azar en cada envío. Sin garantía de equilibrio exacto.' }
  ]

  const sessionTypes = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram_bot', label: 'Telegram Bot' },
    { value: 'email', label: 'Email' }
  ]

  onMount(() => {
    loadPools()
    loadSessions()
  })

  async function loadPools() {
    loading = true
    try {
      const result = await api.get('/api/v1/pools')
      pools = Array.isArray(result) ? result : []
    } catch (e: any) {
      addToast('Error cargando pools: ' + e.message, 'error')
      pools = []
    } finally {
      loading = false
    }
  }

  async function loadSessions() {
    try {
      const result = await api.get('/api/v1/sessions')
      sessions = Array.isArray(result) ? result : []
    } catch (e: any) {
      addToast('Error cargando sesiones: ' + e.message, 'error')
      sessions = []
    }
  }

  async function loadPoolDetails(poolId: string) {
    try {
      const pool = await api.get(`/api/v1/pools/${poolId}`)
      selectedPool = pool
    } catch (e: any) {
      addToast('Error cargando detalles del pool: ' + e.message, 'error')
    }
  }

  async function createPool() {
    if (!createForm.name) {
      addToast('El nombre del pool es requerido', 'error')
      return
    }

    try {
      await api.post('/api/v1/pools', createForm)
      addToast('Pool creado exitosamente', 'success')
      showCreateModal = false
      createForm = { name: '', description: '', mode: 'round-robin', session_type: 'whatsapp' }
      await loadPools()
    } catch (e: any) {
      addToast('Error creando pool: ' + e.message, 'error')
    }
  }

  async function updatePoolActive(pool: Pool, active: boolean) {
    try {
      await api.put(`/api/v1/pools/${pool.id}/active`, { active })
      pool.active = active
      addToast(`Pool ${active ? 'activado' : 'desactivado'} exitosamente`, 'success')
      await loadPools()
    } catch (e: any) {
      addToast('Error actualizando pool: ' + e.message, 'error')
    }
  }

  async function deletePool(poolId: string) {
    if (!confirm('¿Estás seguro de eliminar este pool? Las sesiones no se eliminarán, solo se quitarán del pool.')) {
      return
    }

    try {
      await api.delete(`/api/v1/pools/${poolId}`)
      addToast('Pool eliminado exitosamente', 'success')
      selectedPool = null
      await loadPools()
    } catch (e: any) {
      addToast('Error eliminando pool: ' + e.message, 'error')
    }
  }

  async function addMember() {
    if (!selectedPool || !addMemberForm.session_id) {
      addToast('Selecciona una sesión', 'error')
      return
    }

    // Verificar que la sesión sea del mismo tipo
    const session = sessions.find(s => s.id === addMemberForm.session_id)
    if (!selectedPool) return
    
    if (session && session.type !== selectedPool.session_type) {
      addToast(`La sesión debe ser de tipo ${selectedPool.session_type}`, 'error')
      return
    }

    try {
      await api.post(`/api/v1/pools/${selectedPool.id}/members`, addMemberForm)
      addToast('Sesión agregada al pool', 'success')
      showAddMemberModal = false
      addMemberForm = { session_id: '', priority: 1 }
      await loadPoolDetails(selectedPool.id)
      await loadPools()
    } catch (e: any) {
      addToast('Error agregando sesión: ' + e.message, 'error')
    }
  }

  async function removeMember(poolId: string, sessionId: string) {
    if (!confirm('¿Quitar esta sesión del pool?')) return

    try {
      await api.delete(`/api/v1/pools/${poolId}/members/${sessionId}`)
      addToast('Sesión removida del pool', 'success')
      if (selectedPool) {
        await loadPoolDetails(selectedPool.id)
      }
      await loadPools()
    } catch (e: any) {
      addToast('Error removiendo sesión: ' + e.message, 'error')
    }
  }

  async function updatePriority(poolId: string, sessionId: string, priority: number) {
    try {
      await api.put(`/api/v1/pools/${poolId}/members/${sessionId}/priority`, { priority })
      addToast('Prioridad actualizada', 'success')
      if (selectedPool) {
        await loadPoolDetails(selectedPool.id)
      }
    } catch (e: any) {
      addToast('Error actualizando prioridad: ' + e.message, 'error')
    }
  }

  async function loadStats(poolId: string) {
    try {
      const stats = await api.get(`/api/v1/pools/${poolId}/stats`)
      poolStats = stats.load_stats || {}
      showStatsModal = true
    } catch (e: any) {
      addToast('Error cargando estadísticas: ' + e.message, 'error')
    }
  }

  function getModeLabel(mode: string): string {
    return modes.find(m => m.value === mode)?.label || mode
  }

  function getSessionTypeLabel(type: string): string {
    return sessionTypes.find(t => t.value === type)?.label || type
  }

  function getAvailableSessions(pool: Pool): Session[] {
    const memberIds = new Set(pool.members?.map(m => m.session_id) || [])
    return sessions.filter(s => 
      s.type === pool.session_type && 
      !memberIds.has(s.id) &&
      s.active
    )
  }

  function getConnectedSessionsCount(pool: Pool): number {
    return pool.members?.filter(m => m.session?.connected).length || 0
  }

  function getTotalMembers(pool: Pool): number {
    return pool.members?.length || 0
  }
</script>

<div class="max-w-7xl mx-auto p-6">
  <!-- Header -->
  <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
    <div>
      <h1 class="text-3xl font-black text-white mb-2">
        <span class="bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
          Session Pools
        </span>
      </h1>
      <p class="text-gray-400">
        Distribuye mensajes automáticamente entre múltiples sesiones del mismo tipo.
      </p>
    </div>
    <button
      on:click={() => showCreateModal = true}
      class="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-200 shadow-lg shadow-pink-500/20 flex items-center gap-2"
    >
      <span class="text-xl">+</span>
      Crear Pool
    </button>
  </div>

  <!-- Info Card -->
  <div class="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 backdrop-blur-sm">
    <h3 class="text-lg font-semibold text-white mb-2">¿Cómo funcionan los Pools?</h3>
    <p class="text-gray-400 text-sm mb-4">
      Los pools permiten distribuir la carga entre varias sesiones. Las apps externas siguen usando el mismo <code class="bg-white/10 px-2 py-1 rounded">sessionId</code> de siempre, 
      pero el backend automáticamente distribuye los mensajes entre todas las sesiones del pool.
    </p>
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
      {#each modes as mode}
        <div class="bg-white/5 rounded-lg p-3 border border-white/5">
          <div class="font-medium text-white">{mode.label}</div>
          <div class="text-gray-400 text-xs mt-1">{mode.description}</div>
        </div>
      {/each}
    </div>
  </div>

  <!-- Loading State -->
  {#if loading}
    <div class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400"></div>
    </div>
  {:else}
    <!-- Pools Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {#each pools as pool}
        <div 
          class="bg-white/5 border {pool.active ? 'border-white/10' : 'border-red-500/30'} rounded-2xl p-6 backdrop-blur-sm hover:bg-white/10 transition-all duration-200"
          transition:fade
        >
          <!-- Pool Header -->
          <div class="flex items-start justify-between mb-4">
            <div>
              <h3 class="text-xl font-bold text-white">{pool.name}</h3>
              <p class="text-sm text-gray-400">{pool.description || 'Sin descripción'}</p>
            </div>
            <div class="flex items-center gap-2">
              <button
                on:click={() => updatePoolActive(pool, !pool.active)}
                class="px-3 py-1 rounded-full text-xs font-medium transition-colors {pool.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}"
              >
                {pool.active ? 'Activo' : 'Inactivo'}
              </button>
            </div>
          </div>

          <!-- Pool Info -->
          <div class="space-y-2 mb-4">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-400">Tipo:</span>
              <span class="text-white font-medium">{getSessionTypeLabel(pool.session_type)}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-400">Modo:</span>
              <span class="text-white font-medium">{getModeLabel(pool.mode)}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-400">Sesiones:</span>
              <span class="text-white font-medium">
                {getConnectedSessionsCount(pool)} conectadas / {getTotalMembers(pool)} total
              </span>
            </div>
          </div>

          <!-- Members Preview -->
          {#if pool.members && pool.members.length > 0}
            <div class="flex -space-x-2 mb-4">
              {#each pool.members.slice(0, 5) as member}
                <div 
                  class="w-8 h-8 rounded-full border-2 border-gray-800 flex items-center justify-center text-xs font-medium {member.session?.connected ? 'bg-emerald-500 text-white' : 'bg-gray-600 text-gray-300'}"
                  title={member.session?.label || member.session_id}
                >
                  {(member.session?.label || '?').charAt(0).toUpperCase()}
                </div>
              {/each}
              {#if pool.members.length > 5}
                <div class="w-8 h-8 rounded-full border-2 border-gray-800 bg-gray-700 flex items-center justify-center text-xs text-gray-300">
                  +{pool.members.length - 5}
                </div>
              {/if}
            </div>
          {:else}
            <div class="text-sm text-gray-500 italic mb-4">
              Sin sesiones asignadas
            </div>
          {/if}

          <!-- Actions -->
          <div class="flex flex-wrap gap-2">
            <button
              on:click={() => loadPoolDetails(pool.id)}
              class="flex-1 px-3 py-2 bg-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-colors"
            >
              Gestionar
            </button>
            <button
              on:click={() => loadStats(pool.id)}
              class="px-3 py-2 bg-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-colors"
            >
              Stats
            </button>
            <button
              on:click={() => deletePool(pool.id)}
              class="px-3 py-2 bg-red-500/20 text-red-400 text-sm font-medium rounded-lg hover:bg-red-500/30 transition-colors"
            >
              🗑️
            </button>
          </div>
        </div>
      {:else}
        <div class="col-span-full text-center py-12">
          <div class="text-6xl mb-4">📦</div>
          <h3 class="text-xl font-semibold text-white mb-2">No hay pools creados</h3>
          <p class="text-gray-400 mb-4">Crea tu primer pool para distribuir mensajes entre sesiones</p>
          <button
            on:click={() => showCreateModal = true}
            class="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all"
          >
            Crear Pool
          </button>
        </div>
      {/each}
    </div>
  {/if}
</div>

<!-- Create Pool Modal -->
{#if showCreateModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4" transition:fade>
    <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" on:click={() => showCreateModal = false}></div>
    <div class="relative bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" transition:slide>
      <h2 class="text-2xl font-bold text-white mb-6">Crear Nuevo Pool</h2>
      
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-400 mb-1">Nombre *</label>
          <input
            type="text"
            bind:value={createForm.name}
            placeholder="Ej: WhatsApp Producción"
            class="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-pink-500 focus:outline-none"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-400 mb-1">Descripción</label>
          <input
            type="text"
            bind:value={createForm.description}
            placeholder="Descripción opcional"
            class="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-pink-500 focus:outline-none"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-400 mb-1">Tipo de Sesión *</label>
          <select
            bind:value={createForm.session_type}
            class="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-pink-500 focus:outline-none"
          >
            {#each sessionTypes as type}
              <option value={type.value}>{type.label}</option>
            {/each}
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-400 mb-1">Modo de Distribución *</label>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {#each modes as mode}
              <button
                on:click={() => createForm.mode = mode.value}
                class="p-3 rounded-xl border text-left transition-all {createForm.mode === mode.value ? 'border-pink-500 bg-pink-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}"
              >
                <div class="font-medium text-white">{mode.label}</div>
                <div class="text-xs text-gray-400 mt-1">{mode.description}</div>
              </button>
            {/each}
          </div>
        </div>
      </div>

      <div class="flex gap-3 mt-6">
        <button
          on:click={() => showCreateModal = false}
          class="flex-1 px-4 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
        >
          Cancelar
        </button>
        <button
          on:click={createPool}
          class="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium rounded-xl hover:from-pink-600 hover:to-purple-700 transition-colors"
        >
          Crear Pool
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Pool Details Modal -->
{#if selectedPool}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4" transition:fade>
    <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" on:click={() => selectedPool = null}></div>
    <div class="relative bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" transition:slide>
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-2xl font-bold text-white">{selectedPool.name}</h2>
          <p class="text-sm text-gray-400">{selectedPool ? getSessionTypeLabel(selectedPool.session_type) : ''} • {selectedPool ? getModeLabel(selectedPool.mode) : ''}</p>
        </div>
        <button on:click={() => selectedPool = null} class="text-gray-400 hover:text-white text-2xl">&times;</button>
      </div>

      <!-- Stats -->
      <div class="grid grid-cols-3 gap-4 mb-6">
        <div class="bg-white/5 rounded-xl p-4 text-center">
          <div class="text-2xl font-bold text-white">{getTotalMembers(selectedPool)}</div>
          <div class="text-xs text-gray-400">Sesiones</div>
        </div>
        <div class="bg-white/5 rounded-xl p-4 text-center">
          <div class="text-2xl font-bold text-emerald-400">{getConnectedSessionsCount(selectedPool)}</div>
          <div class="text-xs text-gray-400">Conectadas</div>
        </div>
        <div class="bg-white/5 rounded-xl p-4 text-center">
          <div class="text-2xl font-bold {selectedPool?.active ? 'text-blue-400' : 'text-red-400'}">
            {selectedPool?.active ? 'ON' : 'OFF'}
          </div>
          <div class="text-xs text-gray-400">Estado</div>
        </div>
      </div>

      <!-- Members List -->
      <div class="mb-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-white">Sesiones en el Pool</h3>
          {#if getAvailableSessions(selectedPool).length > 0}
            <button
              on:click={() => { addMemberForm = { session_id: '', priority: 1 }; showAddMemberModal = true }}
              class="px-4 py-2 bg-pink-500/20 text-pink-400 text-sm font-medium rounded-lg hover:bg-pink-500/30 transition-colors"
            >
              + Agregar Sesión
            </button>
          {/if}
        </div>

        {#if selectedPool?.members && selectedPool.members.length > 0}
          <div class="space-y-2">
            {#each selectedPool?.members || [] as member, index}
              <div class="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/5">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full {member.session?.connected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-700 text-gray-400'} flex items-center justify-center font-medium">
                    {(member.session?.label || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div class="font-medium text-white">{member.session?.label || 'Sesión desconocida'}</div>
                    <div class="text-sm text-gray-400">{member.session?.number || member.session_id}</div>
                  </div>
                </div>
                
                <div class="flex items-center gap-3">
                  {#if selectedPool?.mode === 'priority'}
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-gray-400">Prioridad:</span>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={member.priority}
                        on:change={(e) => selectedPool && updatePriority(selectedPool.id, member.session_id, parseInt(e.currentTarget.value))}
                        class="w-16 px-2 py-1 bg-white/10 border border-white/10 rounded text-white text-center text-sm"
                      />
                    </div>
                  {:else}
                    <span class="text-xs text-gray-500">#{index + 1}</span>
                  {/if}
                  <button
                    on:click={() => selectedPool && removeMember(selectedPool.id, member.session_id)}
                    class="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Quitar del pool"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            {/each}
          </div>
        {:else}
          <div class="text-center py-8 bg-white/5 rounded-xl">
            <p class="text-gray-400 mb-4">No hay sesiones en este pool</p>
            {#if getAvailableSessions(selectedPool).length > 0}
              <button
                on:click={() => { addMemberForm = { session_id: '', priority: 1 }; showAddMemberModal = true }}
                class="px-4 py-2 bg-pink-500/20 text-pink-400 text-sm font-medium rounded-lg hover:bg-pink-500/30 transition-colors"
              >
                Agregar Primera Sesión
              </button>
            {:else}
              <p class="text-sm text-gray-500">
                No tienes sesiones {selectedPool ? getSessionTypeLabel(selectedPool.session_type) : ''} disponibles.
                <a href="#/sessions" class="text-pink-400 hover:underline">Crear una sesión</a>
              </p>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button
          on:click={() => selectedPool && updatePoolActive(selectedPool, !selectedPool.active)}
          class="flex-1 px-4 py-3 {selectedPool?.active ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'} font-medium rounded-xl transition-colors"
        >
          {selectedPool?.active ? 'Desactivar Pool' : 'Activar Pool'}
        </button>
        <button
          on:click={() => selectedPool = null}
          class="flex-1 px-4 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Add Member Modal -->
{#if showAddMemberModal && selectedPool}
  <div class="fixed inset-0 z-[60] flex items-center justify-center p-4" transition:fade>
    <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" on:click={() => showAddMemberModal = false}></div>
    <div class="relative bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md" transition:slide>
      <h3 class="text-xl font-bold text-white mb-4">Agregar Sesión al Pool</h3>
      
      {#if getAvailableSessions(selectedPool).length > 0}
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-400 mb-2">Selecciona una sesión</label>
            <div class="max-h-60 overflow-y-auto space-y-2">
              {#each getAvailableSessions(selectedPool) as session}
                <button
                  on:click={() => addMemberForm.session_id = session.id}
                  class="w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all {addMemberForm.session_id === session.id ? 'border-pink-500 bg-pink-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}"
                >
                  <div class="w-10 h-10 rounded-full {session.connected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-700 text-gray-400'} flex items-center justify-center font-medium">
                    {session.label.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div class="font-medium text-white">{session.label}</div>
                    <div class="text-sm text-gray-400">{session.number} {session.connected ? '• Conectada' : '• Desconectada'}</div>
                  </div>
                </button>
              {/each}
            </div>
          </div>

          {#if selectedPool?.mode === 'priority'}
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-1">Prioridad (menor = más prioritario)</label>
              <input
                type="number"
                min="1"
                max="100"
                bind:value={addMemberForm.priority}
                class="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-pink-500 focus:outline-none"
              />
            </div>
          {/if}
        </div>

        <div class="flex gap-3 mt-6">
          <button
            on:click={() => showAddMemberModal = false}
            class="flex-1 px-4 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
          >
            Cancelar
          </button>
          <button
            on:click={addMember}
            disabled={!addMemberForm.session_id}
            class="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium rounded-xl hover:from-pink-600 hover:to-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Agregar
          </button>
        </div>
      {:else}
        <p class="text-gray-400 text-center py-4">
          No tienes sesiones disponibles de tipo {getSessionTypeLabel(selectedPool.session_type)}.
        </p>
        <button
          on:click={() => showAddMemberModal = false}
          class="w-full px-4 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
        >
          Cerrar
        </button>
      {/if}
    </div>
  </div>
{/if}

<!-- Stats Modal -->
{#if showStatsModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4" transition:fade>
    <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" on:click={() => showStatsModal = false}></div>
    <div class="relative bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md" transition:slide>
      <h3 class="text-xl font-bold text-white mb-4">Estadísticas del Pool</h3>
      
      {#if Object.keys(poolStats).length > 0}
        <div class="space-y-3">
          {#each Object.entries(poolStats) as [sessionId, count]}
            {@const member = selectedPool?.members?.find(m => m.session_id === sessionId)}
            <div class="flex items-center justify-between bg-white/5 rounded-lg p-3">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-sm font-medium">
                  {(member?.session?.label || '?').charAt(0).toUpperCase()}
                </div>
                <span class="text-white text-sm">{member?.session?.label || sessionId.slice(0, 8)}</span>
              </div>
              <div class="flex items-center gap-2">
                <div class="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    class="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"
                    style="width: {(count / Math.max(...Object.values(poolStats))) * 100}%"
                  ></div>
                </div>
                <span class="text-white font-medium min-w-[3ch]">{count}</span>
              </div>
            </div>
          {/each}
        </div>
        <p class="text-xs text-gray-400 mt-4">
          * Estadísticas de mensajes enviados desde que se inició el servidor
        </p>
      {:else}
        <p class="text-gray-400 text-center py-4">
          Aún no hay estadísticas disponibles para este pool.
        </p>
      {/if}

      <button
        on:click={() => showStatsModal = false}
        class="w-full mt-4 px-4 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
      >
        Cerrar
      </button>
    </div>
  </div>
{/if}
